function initHeaderMenus() {
  const menus = [...document.querySelectorAll('[data-mega-menu]')];
  if (!menus.length) return;
  menus.forEach((menu) => {
    menu.addEventListener('toggle', () => {
      if (menu.open) menus.forEach((other) => { if (other !== menu) other.open = false; });
    });
    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !menu.open) return;
      menu.open = false;
      menu.querySelector('summary').focus();
    });
    menu.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !menu.contains(event.relatedTarget)) menu.open = false;
    });
  });
  document.addEventListener('click', (event) => {
    menus.forEach((menu) => { if (!menu.contains(event.target)) menu.open = false; });
  });
}

class MobileMenu {
  constructor(drawer) {
    this.drawer = drawer;
    this.openButton = document.querySelector('[data-mobile-menu-open]');
    this.closeButtons = drawer.querySelectorAll('[data-mobile-menu-close]');
    this.lastFocus = null;
    this.onKeydown = this.onKeydown.bind(this);
    this.openButton?.addEventListener('click', () => this.open());
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
  }

  open() {
    this.lastFocus = document.activeElement;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.inert = false;
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');
    document.addEventListener('keydown', this.onKeydown);
    this.drawer.querySelector('[data-mobile-menu-close]')?.focus();
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.inert = true;
    this.openButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    document.removeEventListener('keydown', this.onKeydown);
    this.lastFocus?.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
    if (event.key !== 'Tab') return;
    const focusable = [...this.drawer.querySelectorAll('a, button, summary, input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.disabled && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class ProductGallery {
  constructor(gallery) {
    this.gallery = gallery;
    gallery.refineGallery = this;
    this.items = [...gallery.querySelectorAll('[data-gallery-item]')];
    this.variantPhotos = JSON.parse(gallery.querySelector('[data-variant-photos]')?.textContent || '{}');
    this.variantDetails = JSON.parse(gallery.querySelector('[data-variant-photo-details]')?.textContent || '{}');
    gallery.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
      button.addEventListener('click', () => this.show(button.dataset.galleryThumb, button));
    });
    gallery.querySelectorAll('[data-gallery-zoom]').forEach((button) => {
      this.enableZoom(button);
    });
    this.touchStartX = null;
    this.touchStartY = null;
    gallery.querySelector('.product-gallery__stage')?.addEventListener('touchstart', (event) => {
      // A drag on the enlarged photo pans it instead of changing gallery images.
      if (gallery.querySelector('[data-gallery-zoom].is-zoomed')) {
        this.touchStartX = null;
        return;
      }
      this.touchStartX = event.changedTouches[0].clientX;
      this.touchStartY = event.changedTouches[0].clientY;
    }, { passive: true });
    gallery.querySelector('.product-gallery__stage')?.addEventListener('touchend', (event) => {
      if (this.touchStartX === null) return;
      const delta = event.changedTouches[0].clientX - this.touchStartX;
      const verticalDelta = event.changedTouches[0].clientY - this.touchStartY;
      this.touchStartX = null;
      this.touchStartY = null;
      if (Math.abs(delta) < 48 || Math.abs(delta) <= Math.abs(verticalDelta)) return;
      const activeIndex = this.items.findIndex((item) => item.classList.contains('is-active'));
      const nextIndex = delta < 0
        ? Math.min(activeIndex + 1, this.items.length - 1)
        : Math.max(activeIndex - 1, 0);
      const nextId = this.items[nextIndex]?.dataset.galleryItem;
      const nextButton = gallery.querySelector(`[data-gallery-thumb="${nextId}"]`);
      if (nextId && nextButton) this.show(nextId, nextButton);
    }, { passive: true });
    gallery.querySelector('.product-gallery__stage')?.addEventListener('touchcancel', () => {
      this.touchStartX = null;
      this.touchStartY = null;
    }, { passive: true });
  }

  panZoom(button, x, y) {
    button.style.setProperty('--zoom-x', `${Math.max(0, Math.min(100, x))}%`);
    button.style.setProperty('--zoom-y', `${Math.max(0, Math.min(100, y))}%`);
  }

  setZoom(button, zoomed) {
    button.classList.toggle('is-zoomed', zoomed);
    button.setAttribute('aria-pressed', String(zoomed));
    button.setAttribute('aria-label', zoomed ? 'Zoom out of product image' : 'Zoom product image');
    this.panZoom(button, 50, 50);
    const image = button.querySelector('img');
    if (image) {
      image.dataset.originalSizes ||= image.getAttribute('sizes') || '100vw';
      image.setAttribute('sizes', zoomed ? `${Math.ceil(button.getBoundingClientRect().width * 2)}px` : image.dataset.originalSizes);
    }
    const hint = this.gallery.querySelector('[data-gallery-zoom-hint]');
    if (hint) hint.hidden = !zoomed;
  }

  enableZoom(button) {
    let drag = null;
    let dragged = false;
    let pointerType = 'mouse';
    const isZoomed = () => button.classList.contains('is-zoomed');
    const followPointer = (event) => {
      const rect = button.getBoundingClientRect();
      if (rect.width && rect.height) this.panZoom(button, (event.clientX - rect.left) / rect.width * 100, (event.clientY - rect.top) / rect.height * 100);
    };
    button.addEventListener('click', (event) => {
      if (dragged && event.detail !== 0) return;
      const zoomed = !isZoomed();
      this.setZoom(button, zoomed);
      if (zoomed && pointerType === 'mouse' && event.detail > 0) followPointer(event);
    });
    button.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0) return;
      pointerType = event.pointerType;
      dragged = false;
      if (!isZoomed()) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY,
        originX: parseFloat(button.style.getPropertyValue('--zoom-x')),
        originY: parseFloat(button.style.getPropertyValue('--zoom-y')) };
      button.setPointerCapture(event.pointerId);
    });
    button.addEventListener('pointermove', (event) => {
      if (!isZoomed() || !event.isPrimary) return;
      if (drag && drag.id === event.pointerId && Math.hypot(event.clientX - drag.x, event.clientY - drag.y) > 8) dragged = true;
      if (event.pointerType === 'mouse') {
        followPointer(event);
      } else if (drag && drag.id === event.pointerId) {
        const rect = button.getBoundingClientRect();
        // At 2× magnification, one viewport of movement reaches the opposite edge.
        if (rect.width && rect.height) this.panZoom(button,
          drag.originX - (event.clientX - drag.x) / rect.width * 100,
          drag.originY - (event.clientY - drag.y) / rect.height * 100);
      }
    });
    const endDrag = (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      drag = null;
      if (button.hasPointerCapture(event.pointerId)) button.releasePointerCapture(event.pointerId);
    };
    button.addEventListener('pointerup', endDrag);
    button.addEventListener('pointercancel', endDrag);
    button.addEventListener('lostpointercapture', () => { drag = null; });
    button.addEventListener('keydown', (event) => {
      if (!isZoomed()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.setZoom(button, false);
        return;
      }
      const directions = { ArrowLeft: [-10, 0], ArrowRight: [10, 0], ArrowUp: [0, -10], ArrowDown: [0, 10] };
      const direction = directions[event.key];
      if (!direction) return;
      event.preventDefault();
      this.panZoom(button, parseFloat(button.style.getPropertyValue('--zoom-x')) + direction[0], parseFloat(button.style.getPropertyValue('--zoom-y')) + direction[1]);
    });
  }

  showVariant(sku) {
    const photo = this.variantPhotos[sku];
    if (!photo || this.selectedPhotoSku === sku) return;
    const item = this.gallery.querySelector('[data-gallery-item="approved-1"]');
    const thumb = this.gallery.querySelector('[data-gallery-thumb="approved-1"]');
    if (!item) return;
    this.selectedPhotoSku = sku;
    this.selectedPhoto = photo;
    this.show('approved-1', thumb);
    const details = (photo.details || []).map(key => this.variantDetails[key]).filter(Boolean);
    item.classList.toggle('has-variant-details', details.length > 0);
    let detailRail = item.querySelector('[data-variant-details]');
    if (!detailRail) {
      detailRail = document.createElement('div');
      detailRail.className = 'variant-photo-details';
      detailRail.dataset.variantDetails = '';
      item.append(detailRail);
    }
    renderVariantPhotoDetails(detailRail, details);
    [item.querySelector('[data-gallery-zoom]'), thumb].filter(Boolean).forEach(container => {
      container.style.setProperty('--board-photo-scale', photo.scale);
      const image = container.querySelector('img');
      image.removeAttribute('srcset');
      image.src = photo.src;
      image.alt = photo.alt;
    });
    thumb?.setAttribute('aria-label', `View ${photo.alt}`);
    const caption = this.gallery.querySelector('[data-gallery-selected-caption]');
    if (caption) caption.textContent = `${photo.caption || photo.alt} · Top View`;
    this.gallery.dispatchEvent(new CustomEvent('variant-photo:change'));
  }

  show(id, button) {
    this.gallery.querySelectorAll('[data-gallery-zoom]').forEach((zoom) => {
      this.setZoom(zoom, false);
    });
    this.items.forEach((item) => item.classList.toggle('is-active', item.dataset.galleryItem === id));
    this.gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(thumb === button));
    });
  }
}

function enableCommerceControls(root) {
  const controls = root.querySelector('[data-commerce-controls]');
  if (controls) controls.disabled = false;
  const fallback = root.querySelector('[data-commerce-fallback]');
  if (fallback) fallback.hidden = true;
}

function boardOptionsCompatible(selections) {
  return !(selections['Juice Groove'] === 'Bottom Juice Groove' && selections['Handle Style'] === 'Pocket Handles');
}

function findBoardSizeVariant(product, selections) {
  if (!boardOptionsCompatible(selections)) return undefined;
  return product.variants.find((variant) => product.options.every((name, index) => selections[name] === variant.options[index]));
}

function boardSelectionUrl(productUrl, variantId, currentHref, previewTheme) {
  // Keep preview/theme and toolbar parameters when selecting a size or variant.
  const url = new URL(currentHref);
  // Shopify removes the initial preview query after setting its session cookie.
  // Restore it only for the actual unpublished/development theme being viewed.
  if (previewTheme?.id && ['unpublished', 'development'].includes(previewTheme.role)) {
    url.searchParams.set('preview_theme_id', previewTheme.id);
  }
  if (productUrl) url.pathname = new URL(productUrl, url.origin).pathname;
  url.searchParams.delete('variant');
  if (variantId) url.searchParams.set('variant', variantId);
  return url;
}

class ProductForm {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.variants = JSON.parse(root.querySelector('[data-product-variants]').textContent);
    this.variantId = root.querySelector('[name="id"]');
    this.prices = [...document.querySelectorAll('[data-product-price]')];
    this.comparePrice = document.querySelector('[data-product-compare]');
    this.submit = root.querySelector('[type="submit"]');
    this.message = root.querySelector('[data-product-message]');
    this.personalization = root.querySelector('[data-personalization-fields]');
    this.personalizer = null;
    root.addEventListener('refine:personalization-ready', (event) => {
      this.personalizer = event.target.refinePersonalization;
      this.syncPersonalizationBoard();
      this.updateEngraving();
      const loading = root.querySelector('[data-personalization-loading]');
      if (loading) loading.hidden = true;
    });
    root.addEventListener('refine:personalization-change', () => { this.updatePrice(); this.updateConfigurationSummary(); });
    this.engravingRadios = root.querySelectorAll('[name="properties[Engraving]"]');
    this.artworkField = root.querySelector('[data-artwork-field]');
    this.design = root.querySelector('[name="properties[Engraving design]"]');
    this.options = [...root.querySelectorAll('[data-option-select]')];
    this.optionRadios = [...root.querySelectorAll('[data-option-value]')];
    this.addOnInputs = [...root.querySelectorAll('[data-addon]')];
    this.descriptionGroups = [...root.querySelectorAll('[data-description-group]')];
    this.configurationSummaries = [...document.querySelectorAll('[data-configuration-summary]')];
    this.configurationSummary = this.configurationSummaries[0];
    this.boardSize = root.dataset.boardSize;
    this.sizeLinks = [...root.querySelectorAll('[data-board-size-link]')];
    this.sizeProducts = JSON.parse(root.querySelector('[data-board-size-products]')?.textContent || '[]');
    this.selectedSizeProduct = this.sizeProducts.find((product) => String(product.id) === root.dataset.productId);
    this.sizeExamples = [...root.querySelectorAll('[data-size-example]')];
    this.specifications = document.querySelector('[data-board-specifications]');
    this.compatibilityTooltip = root.querySelector('[data-compatibility-tooltip]');
    this.compatibilityTooltipTimer = null;

    this.bindVariantOptions();
    this.addOnInputs.forEach((input) => input.addEventListener('change', () => {
      this.updateOptionDescriptions();
      this.syncPersonalizationBoard();
      this.updatePrice();
      this.updateConfigurationSummary();
    }));
    this.engravingRadios.forEach((radio) => radio.addEventListener('change', () => this.updateEngraving()));
    this.design?.addEventListener('change', () => this.updateArtworkRequirement());
    this.form?.addEventListener('submit', (event) => this.submitForm(event));
    this.sizePageCache = new Map();
    this.sizeRequest = 0;
    if (this.sizeLinks.length) this.sizePageCache.set(root.dataset.productId, Promise.resolve(this.captureSizePage(document)));
    this.root.querySelectorAll('[data-board-size-link], [data-board-style-link]').forEach((link) => link.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.selectLinkedSize(link);
    }));
    this.root.addEventListener('pointerdown', (event) => this.showBlockedOptionMessage(event), true);
    this.root.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      const label = event.target.closest?.('label[for]');
      if (label && event.relatedTarget && label.contains(event.relatedTarget)) return;
      this.showBlockedOptionMessage(event, true);
    }, true);
    this.root.addEventListener('pointerout', (event) => this.hideBlockedOptionMessage(event), true);
    this.updateVariant();
    this.updateEngraving();
    enableCommerceControls(root);
  }

  bindVariantOptions() {
    this.options.forEach((select) => select.addEventListener('change', () => this.updateVariant()));
    this.optionRadios.forEach((radio) => {
      [...(radio.labels || [])].forEach((label) => label.addEventListener('click', (event) => {
        const preview = this.root.closest('.product-page')?.querySelector('.mobile-board-photo');
        // Touch/click selection must not invoke the browser's label-focus scrolling.
        // Keyboard activation keeps its native behavior and focus navigation.
        if (event.detail === 0 || radio.disabled || !preview || preview.hidden) return;
        event.preventDefault();
        radio.focus({ preventScroll: true });
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }));
    });
    this.optionRadios.forEach((radio) => radio.addEventListener('change', () => {
      if (!radio.checked) return;
      const group = radio.closest('[data-option-group]');
      group?.querySelectorAll('[data-option-value]').forEach((candidate) => { candidate.checked = candidate === radio; });
      const select = group?.querySelector('[data-option-select]');
      if (select) select.value = radio.value;
      this.updateVariant();
    }));
  }

  captureSizePage(doc) {
    const form = doc.querySelector('[data-product-form]');
    const gallery = doc.querySelector('.product-gallery__inner');
    if (!form || !gallery) throw new Error('Board options could not be loaded.');
    return {
      id: form.dataset.productId, title: form.dataset.productTitle,
      url: doc.querySelector('link[rel="canonical"]')?.href || window.location.href,
      sizeLabel: form.dataset.boardSize || '',
      options: [...form.querySelectorAll('[data-option-select]')].map(select => select.dataset.optionName),
      variants: JSON.parse(form.querySelector('[data-product-variants]').textContent),
      specifications: doc.querySelector('[data-board-specifications]')?.innerHTML || '',
      groups: [...form.querySelectorAll('[data-option-group]')].map(group => group.cloneNode(true)),
      photos: [...gallery.children].filter(node => !node.matches('[data-rk-preview-host], [data-rk-view-toggle]')).map(node => node.cloneNode(true)),
      photoSource: form.querySelector('[name="properties[_Photo source]"]')?.value || ''
    };
  }

  async selectLinkedSize(link) {
    const id = link.dataset.boardSizeLink || link.dataset.boardStyleLink;
    const request = ++this.sizeRequest;
    const choices = this.root.querySelector('#board-size-options');
    const status = this.root.querySelector('[data-size-status]');
    const finish = message => {
      choices?.removeAttribute('aria-busy');
      if (status) status.textContent = message;
    };
    if (id === this.root.dataset.productId) { finish(''); return; }
    choices?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Updating your board…';
    try {
      if (!this.sizePageCache.has(id)) {
        const url = boardSelectionUrl(link.href, null, window.location.href, window.Shopify?.theme);
        url.hash = '';
        this.sizePageCache.set(id, fetch(url, { credentials: 'same-origin' }).then(response => {
          if (!response.ok) throw new Error('Unable to load this size.');
          return response.text();
        }).then(html => {
          const page = this.captureSizePage(new DOMParser().parseFromString(html, 'text/html'));
          if (String(page.id) !== String(id)) throw new Error('Unable to load this size.');
          page.url = url.toString();
          return page;
        }).catch(error => { this.sizePageCache.delete(id); throw error; }));
      }
      const page = await this.sizePageCache.get(id);
      if (request !== this.sizeRequest) return;
      const activePhoto = page.photos.map(node => node.querySelector('.is-active img')).find(Boolean);
      if (activePhoto) {
        const image = new Image();
        image.srcset = activePhoto.srcset; image.sizes = activePhoto.sizes; image.src = activePhoto.src;
        await image.decode().catch(() => {});
      }
      if (request !== this.sizeRequest) return;
      // Capture the visible anchor after loading, so scrolling during a request is respected.
      const anchorTop = choices?.getBoundingClientRect().top;
      const selections = this.selectedOptionNames();
      const groups = page.groups.map(group => group.cloneNode(true));
      groups.forEach(group => {
        const select = group.querySelector('[data-option-select]');
        const previous = selections[select.dataset.optionName];
        if ([...select.options].some(option => option.value === previous)) select.value = previous;
        group.querySelectorAll('[data-option-value]').forEach(radio => { radio.checked = radio.value === select.value; });
      });
      const oldGroups = [...this.root.querySelectorAll('[data-option-group]')];
      oldGroups[0]?.before(...groups);
      oldGroups.forEach(group => group.remove());
      this.options = [...this.root.querySelectorAll('[data-option-select]')];
      this.optionRadios = [...this.root.querySelectorAll('[data-option-value]')];
      this.descriptionGroups = [...this.root.querySelectorAll('[data-description-group]')];
      this.bindVariantOptions();
      const gallery = this.root.closest('.product-page').querySelector('.product-gallery');
      const inner = gallery.querySelector('.product-gallery__inner');
      [...inner.children].filter(node => !node.matches('[data-rk-preview-host], [data-rk-view-toggle]')).forEach(node => node.remove());
      inner.append(...page.photos.map(node => node.cloneNode(true)));
      new ProductGallery(gallery);
      const source = this.root.querySelector('[name="properties[_Photo source]"]');
      if (source) source.value = page.photoSource;
      const index = this.sizeProducts.findIndex(product => String(product.id) === String(id));
      if (index === -1) this.sizeProducts.push(page); else this.sizeProducts[index] = page;
      this.root.querySelector('[data-product-variants]').textContent = JSON.stringify(page.variants);
      this.selectSizeProduct(id);
      this.root.querySelectorAll('[data-board-style-link]').forEach(card => {
        const selected = card.dataset.boardStyleLink === id;
        card.classList.toggle('is-selected', selected);
        const label = card.querySelector('[data-style-selected-label]');
        if (label) label.textContent = selected ? 'Selected' : '';
      });
      finish('');
      if (choices) window.scrollBy({ top: choices.getBoundingClientRect().top - anchorTop, behavior: 'instant' });
    } catch (error) {
      if (request === this.sizeRequest) finish('This size could not load. Please try again. Your current board is unchanged.');
    }
  }

  getSelectedOptions() {
    return this.options.map((select) => select.value);
  }

  selectedOptionNames() {
    return Object.fromEntries((this.options || []).map((select) => [select.dataset.optionName, select.value]));
  }

  showBlockedOptionMessage(event, keepOpen = false) {
    const label = event.target.closest?.('label[for]');
    const input = label?.control || (label?.htmlFor ? document.getElementById(label.htmlFor) : null);
    const message = input?.disabled ? input.dataset.compatibilityMessage : '';
    if (!message || !this.compatibilityTooltip) return;

    const tooltip = this.compatibilityTooltip;
    tooltip.textContent = message;
    tooltip.hidden = false;
    tooltip.style.left = '0px';
    tooltip.style.top = '0px';

    const margin = 12;
    const gap = 14;
    const bounds = tooltip.getBoundingClientRect();
    const pointerX = Number.isFinite(event.clientX) ? event.clientX : window.innerWidth / 2;
    const pointerY = Number.isFinite(event.clientY) ? event.clientY : window.innerHeight / 2;
    const left = Math.max(margin, Math.min(pointerX + gap, window.innerWidth - bounds.width - margin));
    const preferredTop = pointerY + gap;
    const top = preferredTop + bounds.height <= window.innerHeight - margin
      ? preferredTop
      : Math.max(margin, pointerY - bounds.height - gap);
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;

    window.clearTimeout(this.compatibilityTooltipTimer);
    if (!keepOpen) this.compatibilityTooltipTimer = window.setTimeout(() => { tooltip.hidden = true; }, 3600);
  }

  hideBlockedOptionMessage(event) {
    const label = event.target.closest?.('label[for]');
    const input = label?.control || (label?.htmlFor ? document.getElementById(label.htmlFor) : null);
    if (!input?.dataset.compatibilityMessage || !this.compatibilityTooltip) return;
    if (event.relatedTarget && label.contains(event.relatedTarget)) return;
    window.clearTimeout(this.compatibilityTooltipTimer);
    this.compatibilityTooltip.hidden = true;
  }

  updateCompatibility() {
    const selections = this.selectedOptionNames();
    (this.options || []).forEach((select) => {
      const name = select.dataset.optionName;
      const group = select.closest('[data-option-group]');
      if (!group) return;
      let blocked = false;
      group.querySelectorAll('[data-option-value]').forEach((radio) => {
        radio.disabled = !boardOptionsCompatible({ ...selections, [name]: radio.value });
        // An invalid saved URL must still allow changing wood while resolving the conflict.
        if (name !== 'Juice Groove' && name !== 'Handle Style') radio.disabled = false;
        radio.dataset.compatibilityMessage = radio.disabled
          ? (name === 'Juice Groove'
            ? 'Bottom Juice Groove is not available with Pocket Handles. Choose No Handles or Side Handles first.'
            : 'Pocket Handles are not available with a Bottom Juice Groove. Choose No Handles or Side Handles.')
          : '';
        blocked ||= radio.disabled;
      });
      [...select.options].forEach((option) => {
        option.disabled = (name === 'Juice Groove' || name === 'Handle Style') && !boardOptionsCompatible({ ...selections, [name]: option.value });
      });
      const notice = group.querySelector('[data-option-compatibility]');
      if (notice) {
        notice.hidden = !blocked;
        notice.textContent = name === 'Juice Groove'
          ? 'For a bottom juice groove, choose No Handles or Side Handles below.'
          : 'Pocket handles are not available with a bottom juice groove. Choose No Handles or Side Handles.';
      }
    });
    return boardOptionsCompatible(selections);
  }

  updateSizeLinks() {
    if (!this.sizeLinks?.length) return;
    const selections = Object.fromEntries(this.options.map((select) => [select.dataset.optionName, select.value]));
    this.sizeLinks.forEach((link) => {
      const product = this.sizeProducts.find((candidate) => String(candidate.id) === link.dataset.boardSizeLink);
      if (!product) return;
      const variant = findBoardSizeVariant(product, selections);
      const url = boardSelectionUrl(product.url, variant?.id, window.location.href, window.Shopify?.theme);
      url.hash = 'board-size-options';
      link.href = url.toString();
      link.dataset.optionsMatched = String(Boolean(variant));
    });
  }

  selectSizeProduct(productId) {
    const product = this.sizeProducts.find((candidate) => String(candidate.id) === String(productId));
    if (!product) return;
    this.selectedSizeProduct = product;
    this.variants = product.variants;
    this.boardSize = product.sizeLabel;
    this.root.dataset.productTitle = product.title;
    this.root.dataset.productId = String(product.id);
    this.sizeLinks.forEach((link) => {
      if (link.dataset.boardSizeLink === String(product.id)) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    this.sizeExamples.forEach((example) => {
      const selected = example.dataset.sizeExample === String(product.id);
      example.classList.toggle('is-selected', selected);
      example.querySelector('[data-size-selected-label]').textContent = selected ? 'Selected' : '\u00a0';
    });
    if (this.specifications) this.specifications.innerHTML = product.specifications;
    this.updateVariant();
  }

  updateConfigurationSummary() {
    const summaries = this.configurationSummaries?.length ? this.configurationSummaries : (this.configurationSummary ? [this.configurationSummary] : []);
    if (!summaries.length) return;
    const parts = this.getSelectedOptions().filter(Boolean);
    if (this.boardSize) parts.unshift(this.boardSize);
    this.addOnInputs.forEach((input) => {
      if (input.disabled) return;
      const selected = input.tagName === 'SELECT' ? Boolean(input.value) : input.checked;
      if (!selected) return;
      const label = input.dataset?.summaryLabel || (input.tagName === 'SELECT' ? input.selectedOptions[0]?.textContent : input.labels?.[0]?.textContent);
      const text = (label || input.value || '').replace(/\s+/g, ' ').trim();
      if (text) parts.push(text);
    });
    const engraving = [...this.engravingRadios].find((radio) => radio.checked);
    if (engraving) parts.push(engraving.value === 'Free engraving' ? (this.personalizer?.summary() || 'Free personalization') : 'No personalization');
    summaries.forEach((summary) => {
      summary.replaceChildren(...parts.map((text) => {
        const choice = document.createElement('li');
        choice.className = 'sticky-atc__choice';
        choice.textContent = text;
        return choice;
      }));
      summary.hidden = parts.length === 0;
    });
  }

  updateOptionDescriptions() {
    this.descriptionGroups.forEach((group) => {
      const output = group.querySelector('[data-option-description-output]');
      if (!output) return;
      const select = group.querySelector('[data-option-select]');
      const selected = select ? select.selectedOptions[0] : group.querySelector('input:checked');
      const description = selected?.dataset.optionDescription || '';
      if (output.textContent !== description) output.textContent = description;
      output.hidden = !description;
      group.querySelectorAll('[data-option-example]').forEach((example) => {
        example.hidden = selected?.value !== example.dataset.optionExample;
      });
    });
  }

  updateVariant() {
    const compatible = this.updateCompatibility();
    this.updateOptionDescriptions();
    this.updateConfigurationSummary();
    this.updateSizeLinks();
    const selected = this.getSelectedOptions();
    const variant = compatible ? (this.selectedSizeProduct
      ? findBoardSizeVariant(this.selectedSizeProduct, this.selectedOptionNames())
      : this.variants.find((candidate) => candidate.options.every((option, index) => option === selected[index]))) : undefined;
    this.currentVariant = variant;

    if (!variant) {
      this.syncPersonalizationBoard();
      this.variantId.value = '';
      this.submit.disabled = true;
      this.submit.textContent = compatible ? window.refineTheme.strings.unavailable : 'Choose compatible options';
      this.prices.forEach((price) => { price.textContent = window.refineTheme.strings.unavailable; });
      if (this.comparePrice) this.comparePrice.hidden = true;
      return;
    }

    this.variantId.value = variant.id;
    this.root.closest('.product-page')?.querySelector('[data-product-gallery]')?.refineGallery?.showVariant(variant.sku);
    this.submit.disabled = !variant.available;
    this.submit.textContent = variant.available ? window.refineTheme.strings.addToCart : window.refineTheme.strings.soldOut;
    this.updatePrice();
    this.syncPersonalizationBoard();
    if (window.history.replaceState && variant.id) {
      const url = boardSelectionUrl(this.selectedSizeProduct?.url, variant.id, window.location.href, window.Shopify?.theme);
      window.history.replaceState({}, '', url);
    }
  }

  updatePrice() {
    if (!this.currentVariant || !this.prices.length) return;
    let total = this.currentVariant.price;
    let compareTotal = this.currentVariant.compare_at_price || 0;
    let addOnTotal = 0;
    this.addOnInputs.forEach((input) => {
      if (input.disabled) return;
      if (((input.type === 'checkbox' || input.type === 'radio') && input.checked) || (input.tagName === 'SELECT' && input.value)) {
        addOnTotal += Number(input.selectedOptions?.[0]?.dataset.price || input.dataset.price || 0);
      }
    });
    addOnTotal += this.personalizer?.fee() || 0;
    total += addOnTotal;
    compareTotal += addOnTotal;
    this.prices.forEach((price) => { price.textContent = this.formatMoney(total); });
    if (this.comparePrice) {
      const onSale = compareTotal > total;
      this.comparePrice.hidden = !onSale;
      if (onSale) this.comparePrice.textContent = this.formatMoney(compareTotal);
    }
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, window.refineTheme.moneyFormat);
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  }

  syncPersonalizationBoard() {
    if (!this.personalizer) return;
    const feet = this.root.querySelector('[data-addon="feet"]:checked');
    this.personalizer?.setVariant(this.currentVariant, { feet: feet?.dataset.addonKey === 'rubber' ? 'Rubber' : feet?.dataset.addonKey === 'brass' ? 'Brass' : 'None' });
  }

  async loadPersonalization() {
    if (this.personalizer || this.personalizationLoading) return;
    this.personalizationLoading = true;
    const loading = this.root.querySelector('[data-personalization-loading]');
    if (loading) { loading.hidden = false; loading.textContent = 'Loading your design options…'; }
    try {
      if (!window.initRefinePersonalization) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = this.root.dataset.personalizationScript;
          script.onload = resolve; script.onerror = reject;
          document.head.append(script);
        });
      }
      await window.initRefinePersonalization();
    } catch (error) {
      const editor = this.root.querySelector('#refine-builder');
      if (editor) delete editor.dataset.ready;
      if (loading) loading.textContent = 'Design options could not load. Select No personalization, then try again.';
    } finally { this.personalizationLoading = false; }
  }

  updateEngraving() {
    const engraving = [...this.engravingRadios].find((radio) => radio.checked)?.value === 'Free engraving';
    this.updateConfigurationSummary();
    if (!this.personalization) return;
    this.personalization.hidden = !engraving;
    this.personalization.querySelectorAll('input, select, textarea').forEach((field) => {
      field.disabled = !engraving;
    });
    this.personalization.querySelectorAll('[data-engraving-required]').forEach((field) => {
      field.required = engraving;
    });
    if (this.root.querySelector('#refine-builder')) {
      if (engraving && !this.personalizer) this.loadPersonalization();
      this.personalizer?.setEnabled(engraving);
      this.updatePrice();
    } else this.updateArtworkRequirement();
  }

  updateArtworkRequirement() {
    if (!this.design || this.personalization?.hidden) return;
    const selected = this.design.selectedOptions[0];
    const requiresText = selected?.dataset.requiresText !== 'false';
    const text = this.root.querySelector('[data-engraving-text]');
    if (text) text.required = requiresText;
    const instructions = this.root.querySelector('[data-design-instructions]');
    if (instructions) {
      instructions.textContent = selected?.dataset.instructions || '';
      instructions.hidden = !instructions.textContent;
    }
    if (!this.artworkField) return;
    const allowsArtwork = selected?.dataset.allowsArtwork === 'true';
    this.artworkField.closest('[data-artwork-wrap]')?.toggleAttribute('hidden', !allowsArtwork);
    this.artworkField.required = allowsArtwork && selected?.dataset.requiresArtwork === 'true';
    this.artworkField.disabled = !allowsArtwork;
  }

  validate() {
    this.message.textContent = '';
    if (!boardOptionsCompatible(this.selectedOptionNames())) {
      this.message.textContent = 'Pocket handles are not available with a bottom juice groove. Choose No Handles or Side Handles.';
      this.message.focus();
      return false;
    }
    const engraving = [...(this.engravingRadios || [])].some((radio) => radio.checked && radio.value === 'Free engraving');
    const engravingError = engraving ? (!this.personalizer ? 'Please wait for your design options to load.' : this.personalizer.validate()) : '';
    if (engravingError || (this.personalizer?.fee() && !Number(this.root.dataset.extraEngravingVariant))) {
      this.message.textContent = engravingError || 'Second-surface engraving is temporarily unavailable. Please choose one surface.';
      this.message.focus(); return false;
    }
    if (!this.form.reportValidity()) {
      this.message.textContent = 'Please complete the highlighted product options.';
      // reportValidity focuses the invalid field; keep keyboard users there.
      return false;
    }
    if (!this.currentVariant?.available) {
      this.message.textContent = window.refineTheme.strings.unavailable;
      this.message.focus();
      return false;
    }
    return true;
  }

  selectedAddOns(parentToken) {
    const addOns = [];
    const feet = this.root.querySelector('[data-addon="feet"]:checked') || this.root.querySelector('select[data-addon="feet"]');
    const feetKey = feet?.selectedOptions?.[0]?.dataset.addonKey || feet?.dataset.addonKey;
    const boardCare = this.root.querySelector('[data-addon="board-care"]:checked');
    const quantity = Math.max(1, Number(this.form.querySelector('[name="quantity"]')?.value || 1));
    const context = {
      '_Parent product': this.root.dataset.productTitle,
      '_Parent SKU': this.currentVariant.sku || '',
      '_Configuration token': parentToken
    };

    if (feetKey === 'rubber' && Number(window.refineTheme.addOns.rubberFeet.id)) {
      addOns.push({ id: Number(window.refineTheme.addOns.rubberFeet.id), quantity, properties: context });
    }
    if (feetKey === 'brass' && Number(window.refineTheme.addOns.brassFeet.id)) {
      addOns.push({ id: Number(window.refineTheme.addOns.brassFeet.id), quantity, properties: context });
    }
    if (boardCare && !boardCare.disabled && Number(boardCare.dataset.variantId)) {
      addOns.push({ id: Number(boardCare.dataset.variantId), quantity, properties: context });
    }
    if (this.personalizer?.fee() && Number(this.root.dataset.extraEngravingVariant)) {
      addOns.push({ id: Number(this.root.dataset.extraEngravingVariant), quantity, properties: { ...context, '_Add-on kind': 'Engraving', 'Additional surface': 'Second engraved surface' } });
    }
    return addOns;
  }

  async submitForm(event) {
    event.preventDefault();
    if (!this.validate()) return;

    this.submit.disabled = true;
    this.submit.setAttribute('aria-busy', 'true');
    this.message.textContent = 'Adding your configuration…';

    try {
      const parentToken = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const formData = new FormData(this.form);
      const personalization = this.personalizer?.payload();
      if (personalization) {
        Object.entries(personalization.properties).forEach(([key, value]) => formData.set(`properties[${key}]`, value));
        personalization.files.forEach(({name, file}) => formData.set(`properties[${name}]`, file));
      }
      [...formData.keys()].filter((key) => key.startsWith('_visual_option_')).forEach((key) => formData.delete(key));
      // Care is a separate cart item that customers may remove independently.
      formData.delete('properties[Board Care]');
      formData.set('properties[_Configuration token]', parentToken);
      const addOns = this.selectedAddOns(parentToken);
      let requestData = formData;
      if (addOns.length) {
        requestData = new FormData();
        formData.forEach((value, name) => {
          if (name === 'id' || name === 'quantity' || name === 'selling_plan') {
            requestData.append(`items[0][${name}]`, value);
          } else if (name.startsWith('properties[')) {
            requestData.append(`items[0][properties]${name.slice('properties'.length)}`, value);
          }
        });
        addOns.forEach((item, index) => {
          const prefix = `items[${index + 1}]`;
          requestData.append(`${prefix}[id]`, item.id);
          requestData.append(`${prefix}[quantity]`, item.quantity);
          Object.entries(item.properties).forEach(([name, value]) => {
            requestData.append(`${prefix}[properties][${name}]`, value);
          });
        });
      }
      const mainResponse = await fetch(`${window.refineTheme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: requestData
      });
      if (!mainResponse.ok) throw new Error((await mainResponse.json()).description || 'Unable to add product.');
      window.location.assign(window.refineTheme.routes.cart);
    } catch (error) {
      this.submit.disabled = !this.currentVariant?.available || !boardOptionsCompatible(this.selectedOptionNames());
      this.submit.removeAttribute('aria-busy');
      this.message.textContent = error.message || window.refineTheme.strings.error;
      this.message.focus();
    }
  }
}

function initCart() {
  const form = document.querySelector('[data-cart-form]');
  if (!form) return;
  const rows = [...form.querySelectorAll('[data-cart-item]')];
  const groups = new Map();
  rows.forEach((row) => {
    const token = row.dataset.configurationToken;
    if (!token) return;
    if (!groups.has(token)) groups.set(token, []);
    groups.get(token).push(row);
  });
  groups.forEach((group) => {
    // Shopify discounts can split a board into multiple lines sharing one token.
    const parents = group.filter((row) => row.dataset.cartAddon !== 'true');
    if (!parents.length || group.length < 2) return;
    const children = group.filter((row) => row.dataset.cartAddon === 'true' && row.dataset.cartCare !== 'true');
    const sync = () => {
      const quantity = parents.reduce((sum, row) => sum + Number(row.querySelector('[data-cart-quantity]').value || 0), 0);
      children.forEach((row) => { row.querySelector('[data-cart-quantity]').value = String(quantity); });
    };
    children.forEach((row) => { row.querySelector('[data-cart-quantity]').readOnly = true; });
    parents.forEach((row) => {
      const input = row.querySelector('[data-cart-quantity]');
      input.addEventListener('input', sync);
      input.addEventListener('change', sync);
    });
    form.addEventListener('submit', sync);
    sync();
  });
  rows.forEach((row) => row.querySelector('[data-cart-remove]').addEventListener('click', (event) => {
    event.preventDefault();
    const group = row.dataset.cartCare === 'true' ? [row] : (groups.get(row.dataset.configurationToken) || [row]);
    group.forEach((item) => { item.querySelector('[data-cart-quantity]').value = '0'; });
    form.requestSubmit(form.querySelector('[name="update"]'));
  }));
  if (window.refinePackingSlip?.init(form)) enableCommerceControls(form);
}

function initStickyAddToCart() {
  const mainButton = document.querySelector('[data-main-atc]');
  const sticky = document.querySelector('[data-sticky-atc]');
  const stickyButton = sticky?.querySelector('[data-sticky-purchase]');
  const summaryToggle = sticky?.querySelector('[data-purchase-summary-toggle]');
  const gallery = document.querySelector('.product-gallery');
  const galleryInner = document.querySelector('.product-gallery__inner');
  if (!mainButton || !stickyButton || !gallery || !galleryInner) return;

  const reserveSpace = () => {
    document.documentElement.style.setProperty('--purchase-bar-height', `${sticky.getBoundingClientRect().height}px`);
  };
  summaryToggle?.addEventListener('click', () => {
    const expanded = summaryToggle.getAttribute('aria-expanded') !== 'true';
    summaryToggle.setAttribute('aria-expanded', String(expanded));
    summaryToggle.textContent = expanded ? 'Hide Selections' : 'View Selections';
    sticky.classList.toggle('is-summary-open', expanded);
    reserveSpace();
  });
  const updateVisibility = () => {
    const galleryStyle = window.getComputedStyle(galleryInner);
    const bounds = gallery.getBoundingClientRect();
    // Desktop: reveal when the photos reach their CSS sticky position.
    // Mobile: the gallery isn't sticky, so wait until the photos have scrolled past.
    const visible = galleryStyle.position === 'sticky'
      ? bounds.top <= (parseFloat(galleryStyle.top) || 0)
      : bounds.bottom <= 0;
    sticky.classList.toggle('is-visible', visible);
    document.body.classList.toggle('has-purchase-bar', visible);
    reserveSpace();
  };
  const syncButton = () => {
    stickyButton.disabled = mainButton.disabled;
    const busy = mainButton.getAttribute('aria-busy') === 'true';
    stickyButton.textContent = mainButton.disabled || busy ? mainButton.textContent : (stickyButton.dataset.readyLabel || mainButton.textContent);
    stickyButton.setAttribute('aria-busy', String(busy));
  };

  const buttonObserver = new MutationObserver(syncButton);
  buttonObserver.observe(mainButton, { attributes: true, attributeFilter: ['disabled', 'aria-busy'], childList: true, subtree: true });
  stickyButton.addEventListener('click', () => { if (!mainButton.disabled) mainButton.click(); });
  syncButton();
  updateVisibility();
  window.addEventListener('scroll', updateVisibility, { passive: true });
  window.addEventListener('resize', updateVisibility);
  if ('ResizeObserver' in window) new ResizeObserver(reserveSpace).observe(sticky);
}

function renderVariantPhotoDetails(rail, details) {
  rail.replaceChildren();
  rail.hidden = !details.length;
  details.forEach(detail => {
    const figure = document.createElement('figure');
    const frame = document.createElement('div');
    frame.className = 'variant-photo-details__crop';
    const image = document.createElement('img');
    image.src = detail.src; image.alt = detail.alt;
    image.width = 1200; image.height = 1200;
    image.style.width = detail.scale;
    image.style.left = detail.left; image.style.top = detail.top;
    const caption = document.createElement('figcaption');
    caption.textContent = detail.label;
    const note = document.createElement('small');
    note.textContent = 'Detail example';
    caption.append(note);
    frame.append(image); figure.append(frame, caption); rail.append(figure);
  });
}

function initMobileVariantPhoto() {
  const page = document.querySelector('.product-page');
  const gallery = page?.querySelector('[data-product-gallery]');
  const options = page?.querySelector('.product-info');
  if (!gallery?.querySelector('[data-variant-photos]') || !options) return;

  const preview = document.createElement('aside');
  preview.className = 'mobile-board-photo';
  preview.hidden = true;
  preview.setAttribute('aria-label', 'Selected board photo');
  const frame = document.createElement('div');
  frame.className = 'mobile-board-photo__frame';
  const image = document.createElement('img');
  image.width = 1254; image.height = 1254;
  const caption = document.createElement('p');
  caption.className = 'mobile-board-photo__caption';
  const detailRail = document.createElement('div');
  detailRail.className = 'variant-photo-details';
  detailRail.hidden = true;
  const visual = document.createElement('div');
  visual.className = 'mobile-board-photo__visual';
  frame.append(image); visual.append(frame, detailRail); preview.append(visual, caption); page.append(preview);
  let currentPhoto = null;
  let photoHeight = 220;
  const mobile = window.matchMedia('(max-width: 989px)');

  const update = () => {
    const source = gallery.querySelector('[data-gallery-item="approved-1"] img');
    const enabled = Boolean(gallery.querySelector('[data-variant-photos]') && source);
    if (enabled) {
      const src = source.getAttribute('src');
      if (image.getAttribute('src') !== src) image.src = src;
      image.alt = source.alt;
      const controller = gallery.refineGallery;
      const photo = controller?.selectedPhoto;
      caption.textContent = photo?.caption || source.alt.replace(/ — (overhead|top) view$/, '').replaceAll(' / ', ' · ');
      frame.style.setProperty('--mobile-photo-scale', photo?.mobileScale || 1);
      frame.style.setProperty('--mobile-photo-rotation', `${photo?.mobileRotation || 0}deg`);
      if (currentPhoto !== photo) {
        currentPhoto = photo;
        const details = (photo?.details || []).map(key => controller.variantDetails[key]).filter(Boolean);
        renderVariantPhotoDetails(detailRail, details);
        visual.classList.toggle('has-variant-details', details.length > 0);
      }
    }
    const header = document.querySelector('.site-header--sticky');
    const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    page.style.setProperty('--personalization-preview-top', `${top + 8}px`);
    preview.style.top = `${top}px`;
    const stage = gallery.querySelector('.product-gallery__stage');
    const engraving = page.querySelector('#refine-stage-host');
    const builder = page.querySelector('#refine-builder');
    photoHeight = preview.getBoundingClientRect().height || photoHeight;
    // Hand off before the fixed photo can cover the inline engraving preview.
    const engravingVisible = builder && !builder.hidden && engraving && !engraving.hidden &&
      engraving.getBoundingClientRect().top < top + photoHeight + 24 && builder.getBoundingClientRect().bottom > top;
    const visible = enabled && !engravingVisible && mobile.matches && stage?.getBoundingClientRect().bottom <= top && options.getBoundingClientRect().bottom > top + 260;
    preview.hidden = !visible;
    document.documentElement.style.setProperty('--mobile-board-photo-offset', visible ? `${top + preview.getBoundingClientRect().height + 16}px` : engravingVisible ? `${top + engraving.getBoundingClientRect().height + 16}px` : '0px');
  };
  let pending = false;
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; update(); });
  };
  new MutationObserver(schedule).observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'alt'] });
  gallery.addEventListener('variant-photo:change', schedule);
  page.addEventListener('refine:preview-visibility', schedule);
  page.addEventListener('refine:personalization-change', schedule);
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  update();
}

function initFacets() {
  document.querySelectorAll('[data-facets-form]').forEach((form) => {
    form.querySelectorAll('input, select').forEach((input) => input.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.pathname = new URL(form.action).pathname;
      url.searchParams.delete('page');
      form.querySelectorAll('[name]').forEach((field) => url.searchParams.delete(field.name));
      new FormData(form).forEach((value, name) => {
        if (value !== '') url.searchParams.append(name, value);
      });
      window.location.assign(url.toString());
    }));
  });
  document.querySelectorAll('[data-facets-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const facets = document.querySelector('[data-facets]');
      facets?.classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(facets?.classList.contains('is-open')));
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initHeaderMenus();
  const drawer = document.querySelector('[data-mobile-drawer]');
  if (drawer) new MobileMenu(drawer);
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => new ProductGallery(gallery));
  document.querySelectorAll('[data-product-form]').forEach((form) => new ProductForm(form));
  initCart();
  initStickyAddToCart();
  initMobileVariantPhoto();
  initFacets();
  document.documentElement.classList.remove('no-js');
});
